import React, {useMemo} from 'react';
import {useParams} from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import Button from "../../../components/Button";
import {useTranslation} from "react-i18next";
import useModalStore from "../../../store/modal";
import {useMakeRefundDecisionMutation} from "../../../utils/mutations";
import {useFetchRefundQuery} from "../../../utils/queries";
import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import InformationLine from "../../../components/InformationLine";
import StatusBadge from "../../../components/StatusBadge";
import {QueryClient} from "react-query";
import ButtonLink from "../../../components/ButtonLink";
import {toast} from "react-toastify";
import use2FAStatus from "../../../hooks/use2FAStatus";

function RefundView() {
    const {refund_uuid} = useParams();
    const {t} = useTranslation();
    const {setModal, clearModal, otpValue, otpRequired, getOtpValue, isOtpRequired, hideModal} = useModalStore();
    const is2FAEnabled = use2FAStatus();

    const makeRefundDecisionMutation = useMakeRefundDecisionMutation();
    const fetchRefundQuery = useFetchRefundQuery(refund_uuid);
    const refund = useMemo(() => fetchRefundQuery?.isSuccess ? fetchRefundQuery?.data : null, [fetchRefundQuery]);

    const handleRefundDecision = async (uuid, decision) => {
        // decision: Approve External, Approve, Reject
        if (is2FAEnabled?.isLoading) {
            toast.warn('Two-factor authentication status is still loading. Please try again.');
            return;
        }

        if (!is2FAEnabled?.data?.twoFactorEnabled) {
            toast.error('2FA is not enabled for your account');
            return;
        }

        let title, description, refundDecision, confirmText;
        switch (decision) {
            case "approve_external":
                title = "Approve external refund request";
                description = "Are you sure you want to approve this refund request? This action cannot be undone.";
                refundDecision = "approve_external";
                confirmText = "Approve External";
                break;
            case "approve":
                title = "Approve refund request";
                description = "Are you sure you want to approve this refund request? This action cannot be undone. The full amount will be refunded to the customer.";
                refundDecision = "approve";
                confirmText = "Approve";
                break;
            case "reject":
                title = "Reject refund request";
                description = "Are you sure you want to reject this refund request? This action cannot be undone.";
                refundDecision = "reject";
                confirmText = "Reject";
                break;
            default:
                title = "Invalid refund request decision";
                description = "The selected refund decision is not recognized. Please choose a valid option.";
                refundDecision = "unknown";
                confirmText = "Unknown";
        }

        setModal({
            isVisible: true,
            title: title,
            description: description,
            otpRequired: true,
            confirmText: confirmText,
            onConfirm: () => {
                if (isOtpRequired() && !getOtpValue()) {
                    toast.error('Please enter OTP to confirm action.');
                    return;
                }
                makeRefundDecisionMutation.mutate({
                    otp: getOtpValue(),
                    refundUuid: uuid,
                    refundDecision: refundDecision,
                })
                hideModal();
            },
            onCancel: () => {
                hideModal();
            },
        })
    }

    const parsedFormData = useMemo(() => typeof refund?.formData === 'string' ? JSON.parse(refund.formData) : null, [refund]);

    return (
        <DashboardLayout>
            <PageHeader back="/merchant/refunds" title="Refund View"
                        description="This page provides you with the details of the refund request and allows you to take appropriate actions."/>

            {fetchRefundQuery.isLoading && <Loading/>}
            {fetchRefundQuery.isError && <div>Error loading refund details.</div>}

            {fetchRefundQuery.isSuccess &&
                <div className="mt-6 border-t border-gray-100">
                    <dl className="divide-y divide-gray-100">
                        <InformationLine title="Refund Id" value={refund?.refundUuid}/>

                        <InformationLine title="Payment" value={
                            <div className="flex">
                                <ButtonLink secondary={false}
                                        href={`/merchant/payments/view/${refund?.payment?.paymentUuid}`}
                                        target="_blank">
                                View Payment
                            </ButtonLink>
                            </div>
                        }/>
                        <InformationLine title="Payment Status"
                                         value={<StatusBadge status={refund?.payment?.status}/>}/>

                        <InformationLine title="Merchant" value={refund?.merchant?.merchantName || "N/A"}/>
                        <InformationLine title="User" value={refund?.user?.name || "N/A"}/>
                        <InformationLine title="Supplier" value={refund?.supplier?.supplierNname || "N/A"}/>
                        <InformationLine title="Requested Amount" value={refund?.amount || "N/A"}/>
                        <InformationLine title="Refunded Amount" value={refund?.refundedAmount || "N/A"}/>
                        <InformationLine title="Currency" value={refund?.currency?.symbol || "N/A"}/>
                        <InformationLine title="Refund Reference" value={refund?.refundReference || "N/A"}/>
                        <InformationLine title="Reason" value={refund?.reason || "N/A"}/>
                        <InformationLine title="Refunded At" value={refund?.refundedAt || "N/A"}/>

                        <InformationLine title="Refund Status" value={<StatusBadge status={refund?.status}/>}/>

                    </dl>

                    {parsedFormData &&
                        <div className="py-4 sm:grid sm:grid-cols-1 sm:gap-2">
                            <dt className="text-sm font-medium leading-6 text-gray-900">Form Data</dt>
                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 p-3 bg-gray-100 shadow rounded-lg">
                                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead>
                                            <tr>
                                                <th scope="col"
                                                    className="py-3.5 pl-4 ltr:pr-3 rtl:pl-3 ltr:text-left rtl:text-right text-xs font-semibold uppercase text-gray-600 sm:pl-0">
                                                    Key
                                                </th>
                                                <th scope="col"
                                                    className="px-3 py-3.5 ltr:text-left rtl:text-right font-semibold uppercase text-xs text-gray-600">
                                                    Value
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                            {Object.entries(parsedFormData).map(([key, value]) => (
                                                <tr key={key}>
                                                    <td className="whitespace-nowrap py-4 pl-4 ltr:pr-3 rtl:pl-3 text-sm font-medium text-gray-900 sm:pl-0">
                                                        {key.replace(/_/g, ' ')}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {typeof value === 'object' ? (
                                                            <pre
                                                                className="whitespace-pre-wrap text-xs">{JSON.stringify(value, null, 2)}</pre>
                                                        ) : value.startsWith('http://') || value.startsWith('https://') ? (
                                                            <a href={value} target="_blank" rel="noopener noreferrer">
                                                                <button className="text-blue-500 underline">View / Download
                                                                </button>
                                                            </a>
                                                        ) : (
                                                            value
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </dd>
                        </div>
                    }

                    <div className="relative whitespace-nowrap py-4 text-sm sm:pr-0 flex gap-2 items-center flex-wrap">
                        {['pending'].includes(refund?.status) && <Button secondary={true}
                                                                         disabled={makeRefundDecisionMutation.isLoading}
                                                                         onClick={() => handleRefundDecision(refund?.refundUuid, 'reject')}>
                            {t('refund.reject')}
                        </Button>}

                        {['pending', 'approved'].includes(refund?.status) &&
                            <Button secondary={false}
                                    disabled={makeRefundDecisionMutation.isLoading}
                                    onClick={() => handleRefundDecision(refund?.refundUuid, 'approve')}>
                                {refund?.status === 'pending' ? t('refund.approve') : t('refund.check_status')}
                            </Button>}

                        {['pending'].includes(refund?.status) &&
                            <Button secondary={false}
                                    disabled={makeRefundDecisionMutation.isLoading}
                                    onClick={() => handleRefundDecision(refund?.refundUuid, 'approve_external')}>
                                {t('refund.approve_external')}
                            </Button>}
                    </div>

                </div>}
        </DashboardLayout>
    );
}

export default RefundView;